import { useCallback, useEffect, useState, type ReactNode } from "react";

import { makeGetApiCall, makePostApiCall } from "../../shared/makeApiCall";
import { useToast } from "../../contexts/ToastContext";

import ModelErrorComponent from "../../components/modals/ModelErrorComponent";

export type QuickBooksImportType = "customers" | "products";
export type QuickBooksImportState = "queued" | "running" | "completed" | "failed";

export interface QuickBooksImportResult {
  created?: number;
  updated?: number;
  groups_created?: number;
  groups_updated?: number;
  categories?: {
    primary?: number;
    sub?: number;
  };
}

export interface QuickBooksImportStatus {
  id: string;
  type: QuickBooksImportType;
  status: QuickBooksImportState;
  attempts: number;
  max_attempts: number;
  result: QuickBooksImportResult | null;
  error: string | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
  updated_at: string;
}

interface QuickBooksResponse {
  message?: string;
  auth?: string;
  title?: string;
  subtitle?: string;
}

interface QuickBooksImportsResponse extends QuickBooksResponse {
  imports: Record<QuickBooksImportType, QuickBooksImportStatus | null>;
}

interface QuickBooksImportQueuedResponse extends QuickBooksResponse {
  already_running: boolean;
  import: QuickBooksImportStatus;
}

const emptyImports: Record<QuickBooksImportType, QuickBooksImportStatus | null> = {
  customers: null,
  products: null,
};

const errorMessage = (data: QuickBooksResponse, fallback: string) =>
  data.message || data.subtitle || fallback;

export function QuickbooksLoginViewModel(logout: () => void) {
  const { showSuccess } = useToast();
  const [emptyModal, setEmptyModelOpen] = useState<{ open: boolean; childComponent: ReactNode }>({
    open: false,
    childComponent: null,
  });
  const [loading, setLoading] = useState(false);
  const [loadingImports, setLoadingImports] = useState(true);
  const [startingImport, setStartingImport] = useState<QuickBooksImportType | null>(null);
  const [imports, setImports] = useState(emptyImports);

  const showErrorModal = useCallback((title: string, subtitle: string) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent setOpen={setEmptyModelOpen} subtitle={subtitle} title={title} />
      ),
      open: true,
    });
  }, []);

  const refreshImports = useCallback(
    async (showErrors = true) => {
      setLoadingImports(true);
      const callResult = await makeGetApiCall<QuickBooksImportsResponse>("quickbooks/imports");
      setLoadingImports(false);

      if (callResult.success) {
        setImports(callResult.data.imports);
        return;
      }

      if (callResult.errorStatus === 401) {
        logout();
        return;
      }

      if (showErrors) {
        showErrorModal(
          "Could not load import status",
          errorMessage(callResult.data, "Could not load QuickBooks import status.")
        );
      }
    },
    [logout, showErrorModal]
  );

  useEffect(() => {
    void refreshImports(false);
  }, [refreshImports]);

  const onLoginPress = async function () {
    setLoading(true);
    const callResult = await makeGetApiCall<QuickBooksResponse>("quickbooks/connect");
    setLoading(false);

    if (callResult.success) {
      return callResult.data.auth;
    }

    if (callResult.errorStatus === 401) {
      logout();
    }
    showErrorModal(
      "Could not connect QuickBooks",
      errorMessage(callResult.data, "Could not start the QuickBooks connection.")
    );
    return false;
  };

  const onStartImport = async function (type: QuickBooksImportType) {
    setStartingImport(type);
    const callResult = await makePostApiCall<QuickBooksImportQueuedResponse>(
      `quickbooks/imports/${type}`
    );
    setStartingImport(null);

    if (callResult.success) {
      setImports((current) => ({ ...current, [type]: callResult.data.import }));
      showSuccess(
        callResult.data.message ||
          `${type === "customers" ? "Customer" : "Product"} import has been queued.`
      );
      return;
    }

    if (callResult.errorStatus === 401) {
      logout();
    }
    showErrorModal(
      `Could not start ${type} import`,
      errorMessage(callResult.data, `Could not queue the QuickBooks ${type} import.`)
    );
  };

  return {
    emptyModal,
    imports,
    loading,
    loadingImports,
    startingImport,
    setEmptyModelOpen,
    onLoginPress,
    onStartImport,
    refreshImports,
  };
}
